import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Wrench, Zap, Target } from "lucide-react";
import { toast } from "sonner";

const ACCENTS = {
  super_simple: {
    key: "super_simple",
    icon: Zap,
    color: "#059669",
    ring: "border-emerald-500/40",
    bg: "bg-emerald-50",
    glow: "shadow-[0_14px_40px_-18px_rgba(16,185,129,0.6)]",
    label: "text-emerald-600",
    chip: "bg-emerald-100 text-emerald-700 border-emerald-300",
    testid: "solution-card-super-simple",
    tag: "01",
  },
  macgyver_special: {
    key: "macgyver_special",
    icon: Wrench,
    color: "#d97706",
    ring: "border-amber-500/40",
    bg: "bg-amber-50",
    glow: "shadow-[0_14px_40px_-18px_rgba(245,158,11,0.6)]",
    label: "text-amber-600",
    chip: "bg-amber-100 text-amber-700 border-amber-300",
    testid: "solution-card-macgyver-special",
    tag: "02",
  },
  tactical_pro: {
    key: "tactical_pro",
    icon: Target,
    color: "#2563eb",
    ring: "border-blue-500/40",
    bg: "bg-blue-50",
    glow: "shadow-[0_14px_40px_-18px_rgba(59,130,246,0.6)]",
    label: "text-blue-600",
    chip: "bg-blue-100 text-blue-700 border-blue-300",
    testid: "solution-card-tactical-pro",
    tag: "03",
  },
};

export const SolutionCard = ({ type, data, index }) => {
  const a = ACCENTS[type];
  const Icon = a.icon;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = `${data.title}\n\n${data.steps
      .map((s, i) => `${i + 1}. ${s}`)
      .join("\n")}${
      data.materials?.length ? `\n\nMatériel: ${data.materials.join(", ")}` : ""
    }`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Solution copiée !");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Impossible de copier automatiquement.");
    }
  };

  return (
    <motion.div
      data-testid={a.testid}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`relative flex flex-col rounded-xl border ${a.ring} ${a.bg} ${a.glow} p-6 lg:p-7`}
    >
      <span
        className="font-heading absolute right-5 top-4 text-5xl font-900 leading-none opacity-15"
        style={{ color: a.color, fontWeight: 900 }}
      >
        {a.tag}
      </span>

      <div className="mb-4 flex items-center gap-3">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-lg"
          style={{ background: `${a.color}1f`, border: `1px solid ${a.color}55` }}
        >
          <Icon size={22} style={{ color: a.color }} />
        </div>
        <span
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${a.label}`}
        >
          {data.badge}
        </span>
      </div>

      <h3 className="font-heading mb-4 text-xl font-bold uppercase tracking-tight text-slate-900 sm:text-2xl">
        {data.title}
      </h3>

      <ol className="mb-4 flex-1 space-y-3">
        {data.steps.map((step, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-600">
            <span
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold"
              style={{ background: `${a.color}22`, color: a.color }}
            >
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      {data.materials?.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {data.materials.map((m, i) => (
            <span
              key={i}
              className={`rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide ${a.chip}`}
            >
              {m}
            </span>
          ))}
        </div>
      )}

      <button
        data-testid="copy-solution-button"
        onClick={handleCopy}
        className="mt-auto flex items-center justify-center gap-2 rounded-lg border border-[#e0d8c6] bg-white/70 py-2.5 text-xs font-semibold uppercase tracking-widest text-slate-500 transition-colors hover:border-slate-400 hover:bg-white hover:text-slate-700"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? "Copié" : "Copier"}
      </button>
    </motion.div>
  );
};
